import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { Subscription } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';

import { AuthenticationService } from './shared/services/authentication.service';
import { Screens, User } from './shared/models/user';
import { CommonService } from './shared/services/common-service.service';
import { NAVBAR_PROMPTS } from './shared/ui-strings';
import { environment } from '../environments/environment';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

    navbarPrompts = NAVBAR_PROMPTS;
    currentUser: User;
    screens = Screens;
    userName = '';
    screenAccessSet = new Set();
    fetchData = false;
    authenticationSubscription$: Subscription;
    commonSubscription$: Subscription;

    constructor(
        private router: Router,
        private authenticationService: AuthenticationService,
        private commonService: CommonService,
        private cookieService: CookieService
    ) { }

    ngOnInit(): void {
        // FIX: Clean up legacy MSAL cookies on app init to prevent RequestHeaderSectionTooLarge error
        this.cleanupMsalCookies();
        this.monitorCookieSize();
        this.fetchCurrentUser();
    }

    private fetchCurrentUser(): void {
        this.authenticationSubscription$ = this.authenticationService.currentUser
            .subscribe({
                next: (user) => {
                    this.currentUser = user;
                    this.screenAccessSet = new Set(user ? user.HOME : []);
                }
            });
        
        this.commonSubscription$ = this.commonService.name
            .subscribe({
                next: (data) => {
                    if (data.length > 0) {
                        this.userName = data;
                    } else {
                        this.userName = localStorage.getItem('currentUserName');
                    }
                }
            });
    }

    logout(): void {
        this.authenticationService.logout();
        this.router.navigate(['/login']);
    }

    showMenuItem(screen: Screens): boolean {
        return this.screenAccessSet.has(screen);
    }

    /**
     * FIX: Clean up all MSAL-related cookies to prevent RequestHeaderSectionTooLarge error.
     * MSAL can set cookies with names like msal.* or msal.<clientId>.*; remove any cookie
     * whose name contains "msal" so header size stays under CloudFront's 8KB limit.
     */
    private cleanupMsalCookies(): void {
        const knownMsal = ['msal-access-token', 'msal-id-token'];
        knownMsal.forEach(cookieName => {
            if (this.cookieService.get(cookieName)) {
                this.cookieService.delete(cookieName, '/', window.location.hostname);
                this.cookieService.delete(cookieName, '/');
            }
        });
        // Also clear any other cookies whose name contains "msal" (e.g. msal.<clientId>.idtoken)
        try {
            const all = document.cookie.split(';');
            const hostname = window.location.hostname;
            all.forEach((cookie) => {
                const name = cookie.split('=')[0].trim();
                if (name && name.toLowerCase().includes('msal')) {
                    this.cookieService.delete(name, '/', hostname);
                    this.cookieService.delete(name, '/');
                }
            });
        } catch (_) {
            // ignore
        }
    }

    /**
     * Monitor cookie size in development to ensure we stay under CloudFront's 8KB limit
     */
    private monitorCookieSize(): void {
        if (!environment.production) {
            const allCookies = document.cookie;
            const cookieSize = new Blob([allCookies]).size;
            
            if (cookieSize > 6144) { // 6KB warning threshold (8KB is limit)
                console.warn(`⚠️ Cookie size is ${cookieSize} bytes (limit: 8192). Consider cleanup.`);
                console.log('Current cookies:', allCookies);
            } else {
                console.log(`✓ Cookie size: ${cookieSize} bytes (within safe limits)`);
            }
        }
    }

    ngOnDestroy(): void {
        this.authenticationSubscription$?.unsubscribe();
        this.commonSubscription$?.unsubscribe();
    }
}
